import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import { PRODUCT_DATA_STATUS_LABELS, type ProductDataStatus } from "@/lib/equipment/enrichment";
import type { ProductOverview, ProductOverviewRow } from "@/hooks/use-equipment-products";

function StatusPill({ status }: { status: ProductDataStatus | "skipped" }) {
  if (status === "saved") return <StatusBadge tone="reached" label={PRODUCT_DATA_STATUS_LABELS.saved} />;
  if (status === "review") return <StatusBadge tone="near" label={PRODUCT_DATA_STATUS_LABELS.review} />;
  if (status === "failed") return <StatusBadge tone="miss" label={PRODUCT_DATA_STATUS_LABELS.failed} />;
  return (
    <span className="inline-flex items-center text-[11.5px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap bg-muted text-muted-foreground">
      {status === "skipped" ? "Hoppes over" : PRODUCT_DATA_STATUS_LABELS[status]}
    </span>
  );
}

/** Modeller med status, og rader som ikke skal ha produktdata (skjerm K). */
export function ProductDataTable({
  overview,
  busyIds,
  onFetch,
  onReview,
  disabled,
}: {
  overview: ProductOverview;
  busyIds: Set<string>;
  onFetch: (p: ProductOverviewRow) => void;
  onReview: (p: ProductOverviewRow) => void;
  disabled: boolean;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Modell</TableHead>
          <TableHead className="text-right">Rader</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right"><span className="sr-only">Handling</span></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {overview.products.map((p) => {
          const busy = busyIds.has(p.id);
          const status = busy ? "running" : p.status;
          return (
            <TableRow key={p.id}>
              <TableCell className="font-medium">{p.brand_name} {p.model}</TableCell>
              <TableCell className="text-right tabular-nums">{p.itemCount}</TableCell>
              <TableCell><StatusPill status={status} /></TableCell>
              <TableCell className="text-right">
                {status === "review" ? (
                  <Button size="sm" variant="outline" onClick={() => onReview(p)}>Se forslag</Button>
                ) : status !== "running" ? (
                  <Button size="sm" variant="outline" disabled={disabled} onClick={() => onFetch(p)}>
                    {status === "saved" ? "Hent på nytt" : "Hent"}
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
        {overview.skipped.map((s) => (
          <TableRow key={s.id} className="text-muted-foreground">
            <TableCell>{s.name}</TableCell>
            <TableCell className="text-right tabular-nums">1</TableCell>
            <TableCell>
              <span className="flex flex-wrap items-center gap-1.5">
                <StatusPill status="skipped" />
                <span className="text-xs">{s.reason}</span>
              </span>
            </TableCell>
            <TableCell />
          </TableRow>
        ))}
        {overview.products.length === 0 && overview.skipped.length === 0 && (
          <TableRow>
            <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
              Ingen modeller ennå. Modeller opprettes av importen eller med «Hent produktdata» på et utstyrskort.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
